import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Loader2, CheckCircle2, Pencil, Plus, Star } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import CRMOwnershipDialog from './CRMOwnershipDialog';
import OwnershipRecordDialog from '../horses/OwnershipRecordDialog';

const HorsesTab = ({ account }) => {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: ownershipData, isLoading: loading } = useQuery({
    queryKey: QK.horsesTab(account?.id),
    queryFn: async () => {
      const [ownershipRes, horsesRes] = await Promise.all([
        api.get('/ownership-records'),
        api.get('/horses'),
      ]);
      const accountRecords = (ownershipRes.data || []).filter(r => r.entity_id === account.id);
      const horseIds = new Set(accountRecords.map(r => r.horse_id));
      const horseMap = {};
      (horsesRes.data || []).forEach(h => { if (horseIds.has(h.id)) horseMap[h.id] = h; });
      return {
        currentOwnership: accountRecords.filter(r => r.is_current),
        historicalOwnership: accountRecords.filter(r => !r.is_current),
        horses: horseMap,
      };
    },
    enabled: !!account?.id,
  });

  const currentOwnership    = ownershipData?.currentOwnership    ?? [];
  const historicalOwnership = ownershipData?.historicalOwnership ?? [];
  const horses              = ownershipData?.horses              ?? {};

  const invalidateTab = () => qc.invalidateQueries({ queryKey: QK.horsesTab(account?.id) });

  const [selectedRecord, setSelectedRecord] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  const handleRecordClick = (record) => {
    setSelectedRecord(record);
    setDetailOpen(true);
  };

  const handleEditRecord = () => {
    setEditingRecord(selectedRecord);
    setDetailOpen(false);
    setAddDialogOpen(true);
  };

  const handleOwnershipSaved = () => {
    setAddDialogOpen(false);
    setEditingRecord(null);
    invalidateTab();
    toast.success('Ownership record saved successfully');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getHorseName = (horseId) => {
    const horse = horses[horseId];
    return horse?.system_name || horse?.name || 'Unknown Horse';
  };

  const getHorseGender = (horseId) => {
    const horse = horses[horseId];
    if (!horse) return '-';
    
    const age = getHorseAge(horseId);
    const isFilly = horse.sex === 'female' && age && age < 5;
    const isMare = horse.sex === 'female' && age && age >= 5;
    const isColt = horse.sex === 'male' && !horse.castrated && age && age < 5;
    const isGelding = horse.sex === 'male' && horse.castrated;
    const isHorse = horse.sex === 'male' && !horse.castrated && age && age >= 5;
    
    if (isFilly) return 'Filly';
    if (isMare) return 'Mare';
    if (isColt) return 'Colt';
    if (isGelding) return 'Gelding';
    if (isHorse) return 'Horse';
    return horse.sex || '-';
  };

  const getHorseAge = (horseId) => {
    const horse = horses[horseId];
    if (!horse?.birthdate) return null;
    
    const currentYear = new Date().getFullYear();
    const birthYear = new Date(horse.birthdate).getFullYear();
    return currentYear - birthYear;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Current Ownership */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Current Horse Ownership</CardTitle>
            <Button
              onClick={() => setAddDialogOpen(true)}
              variant="ghost"
              size="sm"
            >
              <Plus className="w-4 h-4 mr-2" /> Add
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {currentOwnership.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p className="text-sm">No current horse ownership</p>
            </div>
          ) : (
            <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
              <div className="space-y-2" style={{ minWidth: '520px' }}>
                {/* Column Headers */}
                <div className="grid grid-cols-7 gap-4 pb-2 border-b font-semibold text-xs text-muted-foreground uppercase">
                  <div>Horse</div>
                  <div>Gender</div>
                  <div>Age</div>
                  <div>Ownership %</div>
                  <div>Notable for Owner</div>
                  <div>Top Horse</div>
                  <div>Date Acquired</div>
                </div>
              
              {/* Data Rows */}
              {currentOwnership.map((record) => (
                <div
                  key={record.id}
                  className="grid grid-cols-7 gap-4 p-3 border-b last:border-b-0 hover:bg-muted/30 transition-colors cursor-pointer"
                  onClick={() => handleRecordClick(record)}
                >
                  <div className="flex items-center gap-2">
                    {record.is_primary_owner && <CheckCircle2 className="w-4 h-4 text-primary" />}
                    <p className="text-sm font-medium text-primary hover:underline cursor-pointer" onClick={(e) => { e.stopPropagation(); navigate(`/horses/${record.horse_id}`); }}>{getHorseName(record.horse_id)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{getHorseGender(record.horse_id)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{getHorseAge(record.horse_id) || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm">{record.ownership_percent}%</p>
                  </div>
                  <div>
                    {record.is_notable && (
                      <Badge variant="outline" className="text-xs">Notable</Badge>
                    )}
                  </div>
                  <div>
                    {horses[record.horse_id]?.is_top_horse && (
                      <Badge variant="default" className="bg-yellow-600 text-xs">Top</Badge>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{formatDate(record.date_acquired)}</p>
                  </div>
                </div>
              ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Historical Ownership */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Historical Horse Ownership</CardTitle>
            <Button
              onClick={() => setAddDialogOpen(true)}
              variant="ghost"
              size="sm"
            >
              <Plus className="w-4 h-4 mr-2" /> Add
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {historicalOwnership.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p className="text-sm">No historical horse ownership</p>
            </div>
          ) : (
            <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
              <div className="space-y-2" style={{ minWidth: '520px' }}>
                {/* Column Headers */}
                <div className="grid grid-cols-7 gap-4 pb-2 border-b font-semibold text-xs text-muted-foreground uppercase">
                  <div>Horse</div>
                  <div>Gender</div>
                  <div>Age</div>
                  <div>Ownership %</div>
                  <div>Notable for Owner</div>
                  <div>Top Horse</div>
                  <div>Date Sold</div>
                </div>
              
                {/* Data Rows */}
                {historicalOwnership.map((record) => (
                  <div
                    key={record.id}
                    className="grid grid-cols-7 gap-4 p-3 border-b last:border-b-0 hover:bg-muted/30 transition-colors cursor-pointer"
                    onClick={() => handleRecordClick(record)}
                  >
                    <div>
                      <p className="text-sm font-medium text-primary hover:underline cursor-pointer" onClick={(e) => { e.stopPropagation(); navigate(`/horses/${record.horse_id}`); }}>{getHorseName(record.horse_id)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">{getHorseGender(record.horse_id)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">{getHorseAge(record.horse_id) || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm">{record.ownership_percent}%</p>
                    </div>
                    <div>
                      {record.is_notable && (
                        <Badge variant="outline" className="text-xs">Notable</Badge>
                      )}
                    </div>
                    <div>
                      {horses[record.horse_id]?.is_top_horse && (
                        <Badge variant="default" className="bg-yellow-600 text-xs">Top</Badge>
                      )}
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">{formatDate(record.date_sold)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle>Horse Ownership Details</DialogTitle>
            <Button 
              variant="outline" 
              size="icon"
              onClick={handleEditRecord}
              title="Edit on Horse page"
            >
              <Pencil className="w-4 h-4" />
            </Button>
          </DialogHeader>
          {selectedRecord && (
            <div className="space-y-4 py-4">
              {/* Badges */}
              <div className="flex gap-2">
                <Badge variant={selectedRecord.is_current ? 'default' : 'outline'}>
                  {selectedRecord.is_current ? 'Current' : 'Historical'}
                </Badge>
                {selectedRecord.is_primary_owner && (
                  <Badge className="bg-primary hover:bg-primary/90">
                    Primary Owner
                  </Badge>
                )}
                {selectedRecord.is_notable && (
                  <Badge variant="outline">Notable</Badge>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Horse</p>
                  <p className="font-medium">{getHorseName(selectedRecord.horse_id)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Ownership %</p>
                  <p className="font-medium">{selectedRecord.ownership_percent}%</p>
                </div>
                
                {selectedRecord.date_acquired && (
                  <div>
                    <p className="text-xs text-muted-foreground">Date Acquired</p>
                    <p className="font-medium">{formatDate(selectedRecord.date_acquired)}</p>
                  </div>
                )}
                
                {selectedRecord.date_sold && (
                  <div>
                    <p className="text-xs text-muted-foreground">Date Sold</p>
                    <p className="font-medium">{formatDate(selectedRecord.date_sold)}</p>
                  </div>
                )}
                
                {selectedRecord.purchase_price && (
                  <div>
                    <p className="text-xs text-muted-foreground">Purchase Price</p>
                    <p className="font-medium">${selectedRecord.purchase_price.toLocaleString()}</p>
                  </div>
                )}
                
                {selectedRecord.sale_price && (
                  <div>
                    <p className="text-xs text-muted-foreground">Sale Price</p>
                    <p className="font-medium">${selectedRecord.sale_price.toLocaleString()}</p>
                  </div>
                )}
              </div>
              
              {selectedRecord.is_notable && selectedRecord.notable_reason && (
                <div>
                  <p className="text-xs text-muted-foreground">Notable Reason</p>
                  <p className="font-medium">{selectedRecord.notable_reason}</p>
                </div>
              )}
              
              {selectedRecord.notes && (
                <div>
                  <p className="text-xs text-muted-foreground">Notes</p>
                  <p className="text-sm mt-1">{selectedRecord.notes}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Ownership Dialog */}
      <CRMOwnershipDialog
        open={addDialogOpen}
        onClose={() => {
          setAddDialogOpen(false);
          setEditingRecord(null);
        }}
        onSuccess={handleOwnershipSaved}
        account={account}
        record={editingRecord}
      />
    </div>
  );
};

export default HorsesTab;
